import React, { useEffect, useState } from "react";
import AttendanceModal from "../../../records/attendance-modal";
import Attendance from "./Attendance";
import {
  fetchStudentAttendanceService,
  fetchStudentAttendanceStatusService,
} from "../../../../services/users.services";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  ExternalLink,
  FileText,
  Github,
  Plus,
  Sparkles,
} from "lucide-react";
import axios from "axios";
import "./SectionOne.css";
import LockedCard from "../LockedCard";
import { useNavigate } from "react-router-dom";
import { USER_CATEGORY_TO_VERTICAL_MAPPING } from "../Dashboard Features/Resource Center/ResourceCenter";

// Import the mapping constant


const Card = ({ children, className = "" }) => {
  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white text-gray-950 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
};

const CardContent = ({ children, className = "" }) => {
  return <div className={`p-6 pt-0 ${className}`}>{children}</div>;
};

const CardHeader = ({ children, className = "" }) => {
  return <div className={`flex space-y-1.5 p-6 ${className}`}>{children}</div>;
};

const CardTitle = ({ children, className = "" }) => {
  return (
    <h3
      className={`text-2xl font-semibold leading-none tracking-tight ${className}`}
    >
      {children}
    </h3>
  );
};

// Custom Button Component to replace Shadcn Button
const Button = ({
  variant = "default",
  size = "default",
  className = "",
  children,
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

  const variantClasses = {
    default: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
    outline:
      "border border-gray-300 bg-transparent hover:bg-gray-50 focus:ring-gray-300",
    ghost: "hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-300",
  };

  const sizeClasses = {
    default: "h-10 px-4 py-2",
    sm: "h-9 rounded-md px-3 text-sm",
    lg: "h-11 rounded-md px-8",
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

function ResourceLibrary() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [table, setTable] = useState([]);
  const [showAttendance, setShowAttendance] = useState(false);
  const [attendanceType, setAttendanceType] = useState("offline");
  const [isAttendanceActivated, setIsAttendanceActivated] = useState(false);
  const [resourcesData, setResourcesData] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const isLocked = localStorage.getItem("status") !== "active";
  const category_id = Number(localStorage.getItem("category"));

  // UPDATED: Category type mapping for resource types - matches DB subcategory names exactly
  const RESOURCE_CATEGORIES = {
    articles: { subcategory_name: "Article", icon: FileText }, // Changed from "Articles" to "Article"
    books: { subcategory_name: "E-Books", icon: BookOpen }, // Changed from "Books" to "E-Books"
    case_studies: { subcategory_name: "Case Studies", icon: Github },
    research: { subcategory_name: "Research Paper", icon: FileText }, // Changed from "Research" to "Research Paper"
  };

  // Get icon based on category type
  const getIconForCategory = (category) => {
    switch (category) {
      case "articles":
        return FileText;
      case "books":
        return BookOpen;
      case "case_studies":
        return Github;
      case "research":
        return FileText;
      case "cheat_sheet":
        return BookOpen;
      default:
        return FileText;
    }
  };

  // Get subtitle based on category type
  const getSubtitleForCategory = (category) => {
    switch (category) {
      case "articles":
        return "Article";
      case "books":
        return "Book";
      case "case_studies":
        return "Case Study";
      case "research":
        return "Research";
      case "cheat_sheet":
        return "Cheat Sheet";
      default:
        return "Resource";
    }
  };

  // NEW FUNCTION: Get vertical from user category
  const getVerticalFromUserCategory = (userCategoryId) => {
    for (const [vertical, categoryIds] of Object.entries(
      USER_CATEGORY_TO_VERTICAL_MAPPING
    )) {
      if (categoryIds.includes(userCategoryId)) {
        return vertical;
      }
    }
    return null;
  };

  // NEW FUNCTION: Fetch subcategory IDs for a resource type based on user's vertical
  const fetchSubcategoryIdsForResourceType = async (resourceType) => {
    try {
      const userCategoryId = parseInt(localStorage.getItem("category"));
      if (!userCategoryId) return [];

      // Get the vertical for this user
      const userVertical = getVerticalFromUserCategory(userCategoryId);
      console.log("User vertical:", userVertical); // Debug log
      if (!userVertical) return [];

      // Fetch vertical mapping to get category IDs
      const verticalRes = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/resourcecenter/getverticalmapping`,
        {},
        {
          headers: {
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json",
          },
        }
      );

      const verticalMapping = verticalRes.data;
      console.log("Vertical mapping:", verticalMapping); // Debug log
      const categoryIds =
        verticalMapping[userVertical]?.map((cat) => cat.id) || [];
      console.log("Category IDs for vertical:", categoryIds); // Debug log

      if (categoryIds.length === 0) return [];

      // Fetch subcategories for all category IDs
      const subcategoryRequests = categoryIds.map((catId) =>
        axios.post(
          `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/resourcecenter/getsubcategories`,
          { categoryId: catId },
          {
            headers: {
              "Access-Control-Allow-Origin": "*",
              "Content-Type": "application/json",
            },
          }
        )
      );

      const responses = await Promise.all(subcategoryRequests);

      // Find subcategories matching the resource type
      const targetSubcategoryName =
        RESOURCE_CATEGORIES[resourceType]?.subcategory_name;
      console.log(
        `Looking for subcategories named: ${targetSubcategoryName} for resource type: ${resourceType}`
      ); // Debug log
      const matchingSubcategoryIds = [];

      responses.forEach((res) => {
        console.log("Subcategory response data:", res.data); // Debug log
        if (Array.isArray(res.data)) {
          res.data.forEach((sub) => {
            console.log(`Comparing "${sub.subcategory_name}" with "${targetSubcategoryName}"`); // Debug log
            if (sub.subcategory_name === targetSubcategoryName) {
              matchingSubcategoryIds.push(sub.id);
              console.log(`✅ Match found! Adding ID: ${sub.id}`); // Debug log
            }
          });
        }
      });

      console.log(
        `Matching subcategory IDs for ${resourceType}:`,
        matchingSubcategoryIds
      ); // Debug log
      return matchingSubcategoryIds;
    } catch (error) {
      console.error(
        `Error fetching subcategory IDs for ${resourceType}:`,
        error
      );
      return [];
    }
  };

  // Fetch resources from multiple categories - Fixed version
  const fetchResourcesData = async () => {
    try {
      setResourcesLoading(true);
      console.log("Starting to fetch resources..."); // Debug log

      const category_local = localStorage.getItem("category");
      console.log("Category from localStorage:", category_local); // Debug log

      if (!category_local) {
        console.log("No category found in localStorage"); // Debug log
        setResourcesLoading(false);
        return;
      }

      const categories = ["articles", "books", "case_studies", "research"];
      const resourcePromises = [];

      // UPDATED LOGIC: Fetch subcategory IDs and resources for each type
      for (const categoryType of categories) {
        console.log(`Fetching subcategories for ${categoryType}...`); // Debug log
        const subcategoryIds = await fetchSubcategoryIdsForResourceType(
          categoryType
        );
        console.log(`Subcategory IDs for ${categoryType}:`, subcategoryIds); // Debug log

        if (subcategoryIds.length > 0) {
          const promise = axios({
            method: "post",
            headers: {
              "Access-Control-Allow-Origin": "*",
              "Content-Type": "application/json",
            },
            url: `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/resourcecenter/fetchresourcecenter-v2`,
            data: {
              subcategoryIds: subcategoryIds,
            },
          })
            .then((res) => {
              console.log(`${categoryType} response:`, res.data); // Debug log
              return {
                type: categoryType,
                data: Array.isArray(res.data) ? res.data.slice(0, 2) : [], // Get only 2 resources per category
              };
            })
            .catch((error) => {
              console.error(`Error fetching ${categoryType}:`, error);
              return {
                type: categoryType,
                data: [],
              };
            });
          resourcePromises.push(promise);
        }
      }

      console.log("Making", resourcePromises.length, "API calls"); // Debug log

      const results = await Promise.all(resourcePromises);
      const combinedResources = [];

      results.forEach((result) => {
        if (result.data && result.data.length > 0) {
          result.data.forEach((resource) => {
            combinedResources.push({
              ...resource,
              category: result.type,
              icon: getIconForCategory(result.type),
            });
          });
        }
      });

      console.log("Combined resources:", combinedResources); // Debug log
      setResourcesData(combinedResources);
      setResourcesLoading(false);
    } catch (error) {
      console.error("Error fetching resources:", error);
      setResourcesLoading(false);
    }
  };

  // Attendance React Query hooks
  const { data: attendanceStatus } = useQuery({
    queryFn: () =>
      fetchStudentAttendanceStatusService({
        category: localStorage.getItem("category"),
        program_id: localStorage.getItem("program_id"),
        batch_id: localStorage.getItem("batch_id"),
      }),
    queryKey: ["get-attendance-status"],
    enabled:
      !!localStorage.getItem("category") &&
      !!localStorage.getItem("program_id") &&
      !!localStorage.getItem("batch_id"),
  });

  const { data } = useQuery({
    queryFn: () =>
      fetchStudentAttendanceService({
        category: localStorage.getItem("category"),
        attendanceType: attendanceType,
      }),
    queryKey: ["get-attendance"],
    enabled:
      !!localStorage.getItem("category") &&
      showAttendance &&
      isAttendanceActivated,
  });

  useEffect(() => {
    fetchResourcesData();
  }, []);

  useEffect(() => {
    if (attendanceStatus?.data && attendanceStatus?.data.length > 0) {
      setShowAttendance(true);
      setAttendanceType(attendanceStatus?.data[0].status);
      setIsAttendanceActivated(true);
    } else {
      setShowAttendance(false);
    }
  }, [attendanceStatus]);

  useEffect(() => {
    if (data?.attendance) {
      setTable(data?.attendance?.attendance_details);
    }
  }, [data]);

  const defaultResources = [
    {
      title: "Python Documentation",
      subtitle: "Official docs",
      icon: FileText,
      featured: false,
    },
    {
      title: "Kaggle Datasets",
      subtitle: "Practice datasets",
      icon: BookOpen,
      featured: false,
    },
    {
      title: "GitHub Repository",
      subtitle: "Course materials",
      icon: Github,
      featured: false,
    },
    {
      title: "TensorFlow Tutorials",
      subtitle: "Deep learning guides",
      icon: FileText,
      featured: false,
    },
    {
      title: "Stack Overflow",
      subtitle: "Community Q&A",
      icon: BookOpen,
      featured: false,
    },
    {
      title: "Jupyter Notebooks",
      subtitle: "Interactive coding",
      icon: Github,
      featured: false,
    },
    {
      title: "Data Science Cheatsheets",
      subtitle: "Quick references",
      icon: FileText,
      featured: false,
    },
    {
      title: "Machine Learning Papers",
      subtitle: "Research articles",
      icon: BookOpen,
      featured: false,
    },
    {
      title: "NumPy Reference",
      subtitle: "Array computing",
      icon: FileText,
      featured: false,
    },
    {
      title: "Pandas Cookbook",
      subtitle: "Data manipulation",
      icon: BookOpen,
      featured: false,
    },
  ];

  // Use API data if available, otherwise use default resources
  const resources =
    resourcesData.length > 0
      ? resourcesData.map((resource) => ({
        title: resource.hadding,
        subtitle: getSubtitleForCategory(resource.category),
        icon: resource.icon,
        featured: false,
        file_link: resource.file_link,
        description: resource.description,
      }))
      : [];

  const handleResourceClick = (resource) => {
    if (resource.file_link) {
      window.open(resource.file_link, "_blank");
    }
  };

  return (
    <>
      <div className={`flex flex-col  ${showAttendance && !isLocked && category_id !== 44 && category_id !== 50 && category_id !== 51 ? "gap-4" : "gap-0"}`}>
        <div className="md:block w-full hidden">
          {showAttendance && !isLocked && category_id !== 44 && category_id !== 50 && category_id !== 51 && (
            <Attendance data={data} setIsModalOpen={setIsModalOpen} />
          )}
        </div>
        {/* Learning Resources Card */}
        {isLocked ? (
          <LockedCard tooltipText="Complete your profile to unlock courses">
            {" "}
            <Card
              className={`${showAttendance && !isLocked && category_id !== 44 && category_id !== 50 && category_id !== 51 ? "h-[30rem] md:h-[22.6rem]" : "h-[30rem]"
                } flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm`}
            >
              <CardHeader className="flex-shrink-0 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-purple-500/30 rounded-xl flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-medium text-gray-900">
                        Resource Library
                      </CardTitle>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="flex-1 relative overflow-hidden">
                {resourcesLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : (
                  <div className="h-full overflow-y-auto custom-scrollbar pr-2">
                    <div className="pb-16">
                      <div className="space-y-2">
                        {defaultResources.map((resource, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                            onClick={() => handleResourceClick(resource)}
                            title={resource.description || resource.title}
                          >
                            <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                              <resource.icon className="w-3 h-3 text-gray-500" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-900">
                                {resource.title}
                              </h4>
                              <p className="text-xs text-gray-500">
                                {resource.subtitle}
                              </p>
                            </div>
                            <ExternalLink className="w-4 h-4 text-gray-500" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Bottom Button */}
                <div className="absolute bottom-0 left-0 right-0 pt-4 pb-6 pr-6 pl-6 bg-white pointer-events-none">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-gray-200 hover:bg-gray-50 text-gray-700 pointer-events-auto"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    View All Resources
                  </Button>
                </div>
              </CardContent>
            </Card>
          </LockedCard>
        ) : (
          <Card
            className={`${showAttendance && category_id !== 44 && category_id !== 50 && category_id !== 51 ? "h-[30rem] md:h-[22.6rem]" : "h-[30rem]"
              } flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm`}
          >
            <CardHeader className="flex-shrink-0 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-purple-500/30 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-medium text-gray-900">
                      Resource Library
                    </CardTitle>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex-1 relative overflow-hidden">
              {resourcesLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <div className="h-full overflow-y-auto custom-scrollbar pr-2">
                  <div className="pb-16 h-full">
                    {resources.length === 0 ? (
                      <div className="flex border-2 rounded-xl items-center justify-center h-full">
                        <p className="text-gray-500 text-sm">No resources available</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {resources.map((resource, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                            onClick={() => handleResourceClick(resource)}
                            title={resource.description || resource.title}
                          >
                            <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                              <resource.icon className="w-3 h-3 text-gray-500" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-900">
                                {resource.title}
                              </h4>
                              <p className="text-xs text-gray-500">
                                {resource.subtitle}
                              </p>
                            </div>
                            <ExternalLink className="w-4 h-4 text-gray-500" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bottom Button */}
              <div className="absolute bottom-0 left-0 right-0 pt-4 pb-6 pr-6 pl-6 bg-white pointer-events-none">
                <Button
                  onClick={() => {
                    navigate("/Resources");
                  }}
                  variant="outline"
                  size="sm"
                  disabled={resources.length === 0}
                  className="w-full border-gray-200 hover:bg-gray-50 text-gray-700 pointer-events-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  View All Resources
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {showAttendance && category_id !== 44 && category_id !== 50 && category_id !== 51 && (
        <AttendanceModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          table={table}
        />
      )}
    </>
  );
}

export default ResourceLibrary;
